/**
 * Multi-select country filter for pinned prepared-drops view.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

const GLOBE_EMOJI = '🌐';

export interface CountryFilterMenuProps {
  options: Array<{ countryKey: string; label: string }>;
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
}

export const CountryFilterMenu: FC<CountryFilterMenuProps> = ({ options, selectedKeys, onChange }) => {
  const toggle = (key: string) => {
    if (selectedKeys.includes(key)) {
      onChange(selectedKeys.filter((k) => k !== key));
    } else {
      onChange([...selectedKeys, key]);
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 max-w-[12rem] gap-1.5 px-2 text-xs sm:max-w-none"
          type="button"
          title="Filter drafts by country"
        >
          <span className="shrink-0 text-[15px] leading-none" aria-hidden>
            {GLOBE_EMOJI}
          </span>
          <span className="shrink-0 font-medium">Country</span>
          {selectedKeys.length > 0 ? (
            <span className="flex min-w-0 flex-1 items-center gap-1 text-muted-foreground">
              <span className="shrink-0 tabular-nums">({selectedKeys.length})</span>
              <span className="flex min-w-0 items-center gap-0.5 overflow-hidden" aria-hidden>
                {selectedKeys.slice(0, 5).map((k) => (
                  <span key={k} className="shrink-0 text-[15px] leading-none">
                    {countryToFlagEmoji(k)}
                  </span>
                ))}
              </span>
            </span>
          ) : null}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-[200] min-w-[16rem] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-xs outline-none hover:bg-accent"
            onSelect={() => onChange([])}
          >
            <span className="shrink-0 text-[15px] leading-none" aria-hidden>
              {GLOBE_EMOJI}
            </span>
            <span className="font-medium">All countries</span>
          </DropdownMenu.Item>
          {options.map((o) => {
            const checked = selectedKeys.includes(o.countryKey);
            return (
              <DropdownMenu.CheckboxItem
                key={o.countryKey}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-sm py-2 pl-2 pr-2 text-xs outline-none',
                  'hover:bg-accent data-[state=checked]:bg-primary/10',
                )}
                checked={checked}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => toggle(o.countryKey)}
              >
                <span className="flex w-7 shrink-0 justify-center text-base leading-none" aria-hidden>
                  {countryToFlagEmoji(o.countryKey)}
                </span>
                <span className="min-w-0 flex-1 font-medium text-foreground">{o.label}</span>
              </DropdownMenu.CheckboxItem>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
